import { isEmpty } from 'lodash';
import { useAsyncFn } from 'react-use';

import * as Sentry from 'lib/client/sentry/sentry';
import { postRequestOptions } from 'components/ContentReporting/form-utils';
import { getValidSpotifyUriList, isSpotifyUri } from 'components/ContentReporting/validation-utils';
import { CONTENTREPORTINGAPI_URL } from 'containers/ContentReporting/ContentPolicyReporting/utils/constants';
import { EntityType } from 'proto/generated/inspector_gadget';
import {
  associatedContentTypeFromContentType,
  couldHaveAssetExistenceCheck,
} from 'containers/ContentReporting/ContentPolicyReporting/utils/functions/contentType';
import { ContentType, contentTypeFromJSON } from 'proto/generated/tintin_content_policy';
import { ErrorMessage } from 'containers/ContentReporting/ContentPolicyReporting/utils/types';

export const useAssetExists = () => {
  const [{ value, loading, error }, fetchAssetExists] = useAsyncFn(
    async (reportedUri: string, contentType?: ContentType, entityType?: EntityType) => {
      const [uri] = getValidSpotifyUriList(Array.from(new Set([reportedUri].filter(isSpotifyUri))));

      if (isEmpty(uri) || !couldHaveAssetExistenceCheck({ entityType, contentType })) {
        return false;
      }

      const options = postRequestOptions({
        uri,
        associatedContentType: associatedContentTypeFromContentType(contentTypeFromJSON(contentType)),
      });

      try {
        const response = await fetch(`${CONTENTREPORTINGAPI_URL}/v1/asset-type`, options);
        if (response.ok) {
          const { assetExists } = await response.json();

          return assetExists;
        }

        Sentry.captureException(`Called "/v1/asset-type", Code: ${response.status}; Payload: ${response.body}`);
      } catch (e: unknown) {
        Sentry.captureException(`Error under useAssetExists: ${JSON.stringify(e)}`);
      }
      return Promise.reject(ErrorMessage.CONTENT_REQUEST_ERROR);
    },
    [],
  );

  return {
    isLoading: loading,
    error,
    fetchAssetExists,
    assetExists: value,
  };
};

export default useAssetExists;
